import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import { formatDay, formatMoney } from "@/shared/lib";
import { Panel } from "@/shared/ui";

type CapitalEntry = components["schemas"]["CapitalEntry"];

/**
 * История капитала (ТЗ §20): откуда взялась каждая внесённая сумма.
 *
 * Существует ровно затем, чтобы пополнение было видно КАК ПОПОЛНЕНИЕ. На
 * графике его нет и быть не может — там только торговый результат (§22), — и
 * без этого списка внесённые деньги просто исчезали бы из виду между балансом
 * и капиталом робота.
 *
 * `is_connected` приходит с бэкенда, а не выводится из даты на клиенте: «уже
 * работает» решает тот же расчёт, что и начисляет, включая админскую
 * «прокрутку дня вперёд».
 */
export function CapitalHistoryList({ entries }: { entries: CapitalEntry[] }) {
  const { t } = useTranslation();

  if (entries.length === 0) return null;

  return (
    <Panel as="section" radius="lg" className="flex flex-col gap-3 p-4">
      <h2 className="text-body font-semibold text-text-primary">
        {t("myRobots.capital.title")}
      </h2>

      <ul className="flex flex-col">
        {entries.map((entry, index) => (
          <li
            key={entry.id}
            className={[
              "flex items-start justify-between gap-3 py-2.5",
              index > 0 ? "border-t border-border-subtle" : "",
            ].join(" ")}
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-caption font-medium text-text-primary">
                {t(`myRobots.capital.kind.${entry.kind}`)}
              </span>
              <span className="text-micro font-normal text-text-tertiary">
                {formatDay(entry.at.slice(0, 10))}
              </span>
            </div>

            <div className="flex shrink-0 flex-col items-end gap-0.5">
              <span className="text-caption font-semibold text-text-primary">
                {formatMoney(entry.amount, { sign: "always", suffix: "USDT" })}
              </span>
              <span
                className={[
                  "text-micro font-normal",
                  entry.is_connected ? "text-status-profit" : "text-status-warning",
                ].join(" ")}
              >
                {entry.is_connected
                  ? t("myRobots.capital.connected")
                  : t("myRobots.capital.pending", { date: formatDay(entry.effective_from) })}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
